import {
  buildSyncSuccessMessage,
  normalizeErrorMessage,
} from "./exchangeRatePageUtils";

export function createExchangeRateSyncHandlers(deps) {
  const {
    activeTab,
    baseCurrency,
    supportedCurrencies,
    syncing,
    setSyncing,
    setSyncError,
    showToast,
    syncExchangeRates,
    syncExchangeRatesStream,
    setFullSyncProgress,
    loadSyncStatus,
    loadAutoSyncStatus,
    loadOverview,
    loadMatrix,
    loadPairs,
  } = deps;

  const reloadActiveData = async () => {
    await Promise.all([loadSyncStatus(), loadAutoSyncStatus(), loadOverview()]);
    if (activeTab === "matrix") await loadMatrix();
    if (activeTab === "pairs") await loadPairs();
  };

  const handleManualSync = async () => {
    if (syncing) return;
    setSyncing(true);

    try {
      const symbols = supportedCurrencies.filter(
        (currency) => currency !== baseCurrency,
      );
      const syncResult = await syncExchangeRates({ baseCurrency, symbols });

      await reloadActiveData();
      if (syncResult.ok) {
        showToast({
          id: `exchange-sync-success-${Date.now()}`,
          kind: "notification",
          severity: "success",
          title: "Exchange rates synced",
          message: buildSyncSuccessMessage(syncResult),
        });
      } else {
        showToast({
          id: `exchange-sync-failed-${Date.now()}`,
          kind: "notification",
          severity: "error",
          title: "Exchange rate sync failed",
          message: syncResult.message || "Exchange rate sync failed",
          persistent: true,
        });
      }
    } catch (err) {
      const message = normalizeErrorMessage(
        err,
        "Failed to sync exchange rates.",
      );
      showToast({
        id: `exchange-sync-error-${Date.now()}`,
        kind: "notification",
        severity: "error",
        title: "Exchange rate sync failed",
        message,
        persistent: true,
      });
      setSyncError(message);
      await loadSyncStatus();
      await loadAutoSyncStatus();
    } finally {
      setSyncing(false);
    }
  };

  const handleFullSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setFullSyncProgress?.({
      type: "started",
      processed: 0,
      total: 0,
      remaining: 0,
      syncedCount: 0,
      missingCount: 0,
      failedCount: 0,
      message: "Full exchange-rate sync started",
    });

    try {
      const syncResult = await syncExchangeRatesStream(
        { allBaseCurrencies: true },
        {
          onProgress: (progress) => setFullSyncProgress?.(progress),
        },
      );

      await reloadActiveData();
      showToast({
        id: `exchange-full-sync-${Date.now()}`,
        kind: "notification",
        severity: syncResult.ok ? "success" : "error",
        title: syncResult.ok
          ? "Full exchange-rate sync finished"
          : "Full exchange-rate sync failed",
        message: syncResult.ok
          ? buildSyncSuccessMessage(syncResult)
          : syncResult.message || "Full exchange-rate sync failed",
        persistent: !syncResult.ok,
      });
    } catch (err) {
      const message = normalizeErrorMessage(
        err,
        "Failed to sync all exchange rates.",
      );
      showToast({
        id: `exchange-full-sync-error-${Date.now()}`,
        kind: "notification",
        severity: "error",
        title: "Full exchange-rate sync failed",
        message,
        persistent: true,
      });
      setSyncError(message);
      await loadSyncStatus();
      await loadAutoSyncStatus();
    } finally {
      setSyncing(false);
    }
  };

  return { handleManualSync, handleFullSync };
}
